import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { captureEvent } from 'src/common/analytics';
import TourStyles from './tourStyles';
import TourOverlay from './TourOverlay';
import TourPrompt from './TourPrompt';
import { DEFAULT_TOUR_ID, TOURS } from './tourSteps';

/**
 * Which login we already offered the tour for. `lastLogin` is written by every
 * sign-in path (login2, DemoVerify), so comparing against it gives us "ask once
 * per login" without the auth modules needing to know the tour exists — they
 * wipe storage at import time, so nothing tour-related may live there.
 */
const PROMPT_SEEN_KEY = 'tourPromptSeenFor';
/** Let the page paint (and the grid load) before interrupting with the offer. */
const PROMPT_DELAY_MS = 1400;

/**
 * Which login we already drew attention to the header button for. Same
 * `lastLogin` trick as the prompt above, and for the same reason.
 */
const HIGHLIGHT_SEEN_KEY = 'tourHighlightSeenFor';
/**
 * Starts after the modal prompt would have appeared, so the two never compete
 * for the same glance. If the prompt IS showing, the highlight is suppressed
 * outright and only begins once it has been answered.
 */
const HIGHLIGHT_DELAY_MS = PROMPT_DELAY_MS + 600;
/**
 * How long the button keeps pulsing — roughly three and a half cycles at the
 * 1.9s animation in tourStyles. Bounded on purpose: an indefinite animation in
 * the header stops reading as "look here" and starts reading as a defect, and it
 * competes with whatever the user came to do.
 */
const HIGHLIGHT_MS = 7000;

type TourApi = {
  /** Start a tour, routing to its page first when needed. */
  startTour: (tourId?: string) => void;
  endTour: () => void;
  activeTourId: string | null;
  /**
   * True while the header's "Start tour" button should call attention to itself.
   * Rendered as a pulsing ring by the Header — see tourStyles.
   */
  highlightTour: boolean;
  /**
   * How many documents the listing just loaded. Reported by the document grid
   * because it is the only thing that knows, and it has the number already —
   * having the provider fetch the listing itself would duplicate the request and
   * would fail exactly when the services are down.
   */
  reportDocumentCount: (count: number) => void;
};

const TourContext = createContext<TourApi>({
  startTour: () => {},
  endTour: () => {},
  activeTourId: null,
  highlightTour: false,
  reportDocumentCount: () => {}
});

export const useTour = () => useContext(TourContext);

function TourProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [activeTourId, setActiveTourId] = useState<string | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  /**
   * How many conditional steps this run has dropped (see `onlyWhenVisible`).
   * Only ever counts steps already behind us — a step is judged on entry — so
   * it shifts both the displayed step number and the total, and the footer
   * reads "Step 6 of 15" rather than jumping from 5 to 7.
   */
  const [skippedCount, setSkippedCount] = useState(0);
  // Set when a tour was requested from another page: we navigate first, then
  // start once the target route is mounted.
  const [pendingTourId, setPendingTourId] = useState<string | null>(null);
  const [promptTourId, setPromptTourId] = useState<string | null>(null);
  /** Pulse the header button to point out the tour is there. */
  const [highlightTour, setHighlightTour] = useState(false);
  /**
   * Documents in the listing. `null` means "not known yet" and is deliberately
   * distinct from 0 — the prompt waits for a real answer rather than firing
   * against an empty grid that simply hasn't loaded.
   */
  const [documentCount, setDocumentCount] = useState<number | null>(null);

  const reportDocumentCount = useCallback((count: number) => {
    setDocumentCount(Number.isFinite(count) ? count : null);
  }, []);

  /** On the page a tour STARTS on (used for routing there and for the prompt). */
  const onTourRoute = useCallback(
    (tourId: string) => {
      const match = TOURS[tourId]?.routeMatch;
      return !match || pathname.includes(match);
    },
    [pathname]
  );

  /**
   * Still anywhere the tour is allowed to be. A tour can span several pages (this
   * one follows the document from the list into the review workspace), so it may
   * only be torn down once the path matches none of its routes.
   */
  const inTourScope = useCallback(
    (tourId: string) => {
      const tour = TOURS[tourId];
      if (!tour) return false;
      const scope = tour.routeScope?.length
        ? tour.routeScope
        : [tour.routeMatch].filter(Boolean);
      if (!scope.length) return true;
      return scope.some((fragment) => pathname.includes(fragment as string));
    },
    [pathname]
  );

  /**
   * Record that this login has had its nudge. Called from startTour as well as
   * when the pulse runs, so a user who finds the button on their own is never
   * pulsed at afterwards — otherwise the highlight effect would fire the moment
   * their tour ended.
   */
  const markHighlightSeen = useCallback(() => {
    const lastLogin = localStorage.getItem('lastLogin');
    if (lastLogin) localStorage.setItem(HIGHLIGHT_SEEN_KEY, lastLogin);
    setHighlightTour(false);
  }, []);

  const startTour = useCallback(
    (tourId: string = DEFAULT_TOUR_ID) => {
      const tour = TOURS[tourId];
      if (!tour) return;
      setPromptTourId(null);
      markHighlightSeen();
      captureEvent('tour_started', { tour: tourId });
      if (!onTourRoute(tourId) && tour.routeTo) {
        setPendingTourId(tourId);
        navigate(tour.routeTo);
        return;
      }
      setStepIndex(0);
      setSkippedCount(0);
      setActiveTourId(tourId);
    },
    [navigate, onTourRoute, markHighlightSeen]
  );

  const endTour = useCallback(() => {
    if (activeTourId) {
      captureEvent('tour_dismissed', { tour: activeTourId, step: stepIndex + 1 });
    }
    setActiveTourId(null);
    setStepIndex(0);
    setSkippedCount(0);
  }, [activeTourId, stepIndex]);

  // Start a tour that was requested from a different page, once we're there.
  useEffect(() => {
    if (!pendingTourId || !onTourRoute(pendingTourId)) return;
    const t = setTimeout(() => {
      setStepIndex(0);
      setSkippedCount(0);
      setActiveTourId(pendingTourId);
      setPendingTourId(null);
    }, 600);
    return () => clearTimeout(t);
  }, [pendingTourId, onTourRoute]);

  /**
   * Offer the tour once per login — but only to an EMPTY workspace.
   *
   * A user whose document listing already has rows in it has plainly worked out
   * how the product goes; interrupting them at every sign-in is a cost with no
   * return. An empty listing is the one moment the walkthrough is genuinely the
   * most useful thing on screen, because there is nothing else to do yet.
   *
   * `documentCount === 0` is checked strictly against 0, never for falsiness:
   * `null` means the grid has not answered yet, and treating that as empty would
   * fire the prompt over a still-loading page — the exact interruption the delay
   * below exists to avoid.
   */
  useEffect(() => {
    if (activeTourId || pendingTourId || promptTourId) return;
    const lastLogin = localStorage.getItem('lastLogin');
    if (!lastLogin) return;
    if (localStorage.getItem(PROMPT_SEEN_KEY) === lastLogin) return;
    // Still the tour's own page: that is where the prompt's steps point.
    if (!onTourRoute(DEFAULT_TOUR_ID)) return;
    if (documentCount !== 0) return;
    const t = setTimeout(() => setPromptTourId(DEFAULT_TOUR_ID), PROMPT_DELAY_MS);
    return () => clearTimeout(t);
  }, [activeTourId, pendingTourId, promptTourId, onTourRoute, documentCount]);

  const consumePrompt = useCallback(() => {
    const lastLogin = localStorage.getItem('lastLogin');
    if (lastLogin) localStorage.setItem(PROMPT_SEEN_KEY, lastLogin);
    setPromptTourId(null);
  }, []);

  /**
   * Draw the eye to the header button once per login.
   *
   * The modal prompt is easy to dismiss on reflex, and once it is gone there is
   * nothing telling a new user the walkthrough still exists. This is the quieter
   * second chance: it waits for the prompt to be out of the way (an open modal or
   * a running tour suppresses it outright), then pulses the button briefly.
   *
   * Also covers the case the prompt never reaches: land anywhere other than the
   * tour's own page after signing in and the modal does not fire at all, so the
   * button is the only hint there is.
   */
  useEffect(() => {
    if (activeTourId || pendingTourId || promptTourId) {
      setHighlightTour(false);
      return;
    }
    const lastLogin = localStorage.getItem('lastLogin');
    if (!lastLogin) return;
    if (localStorage.getItem(HIGHLIGHT_SEEN_KEY) === lastLogin) return;

    const t = setTimeout(() => {
      // Marked as spent when the pulse STARTS, so navigating between pages during
      // the same session doesn't restart it on every mount.
      localStorage.setItem(HIGHLIGHT_SEEN_KEY, lastLogin);
      setHighlightTour(true);
    }, HIGHLIGHT_DELAY_MS);
    return () => clearTimeout(t);
  }, [activeTourId, pendingTourId, promptTourId]);

  // Stop after a bounded run — see HIGHLIGHT_MS.
  useEffect(() => {
    if (!highlightTour) return;
    const t = setTimeout(() => setHighlightTour(false), HIGHLIGHT_MS);
    return () => clearTimeout(t);
  }, [highlightTour]);

  const activeTour = activeTourId ? TOURS[activeTourId] : null;

  const next = useCallback(() => {
    if (!activeTour) return;
    if (stepIndex + 1 >= activeTour.steps.length) {
      captureEvent('tour_completed', { tour: activeTour.id });
      setActiveTourId(null);
      setStepIndex(0);
      setSkippedCount(0);
      return;
    }
    setStepIndex(stepIndex + 1);
  }, [activeTour, stepIndex]);

  // A conditional step that doesn't apply to this run (the engine was already
  // up, so there is nothing to wait for): move on and take it out of the count.
  const skipStep = useCallback(() => {
    setSkippedCount((n) => n + 1);
    next();
  }, [next]);

  // Navigating right out of the tour (a sidebar link, say) ends it rather than
  // leaving a spotlight pointing at nothing. Moving BETWEEN the tour's own pages
  // — the document list to the review workspace — is part of the walkthrough and
  // must not.
  useEffect(() => {
    if (activeTourId && !inTourScope(activeTourId)) {
      setActiveTourId(null);
      setStepIndex(0);
      setSkippedCount(0);
    }
  }, [activeTourId, inTourScope]);

  const api = useMemo<TourApi>(
    () => ({
      startTour,
      endTour,
      activeTourId,
      highlightTour,
      reportDocumentCount
    }),
    [startTour, endTour, activeTourId, highlightTour, reportDocumentCount]
  );

  return (
    <TourContext.Provider value={api}>
      <TourStyles />
      {children}
      {promptTourId && TOURS[promptTourId] && (
        <TourPrompt
          tour={TOURS[promptTourId]}
          onStart={() => {
            consumePrompt();
            startTour(promptTourId);
          }}
          onSkip={() => {
            captureEvent('tour_prompt_skipped', { tour: promptTourId });
            consumePrompt();
          }}
        />
      )}
      {activeTour && (
        <TourOverlay
          tour={activeTour}
          stepIndex={stepIndex}
          stepNumber={Math.max(1, stepIndex + 1 - skippedCount)}
          stepTotal={Math.max(1, activeTour.steps.length - skippedCount)}
          onNext={next}
          onSkipStep={skipStep}
          onClose={endTour}
        />
      )}
    </TourContext.Provider>
  );
}

export default TourProvider;
